import React, { useEffect, useState } from "react";
import { api } from "../../api/client";
import { toast } from "sonner";
import { Plus, PencilSimple, LightningSlash, Trash, ClockClockwise, ArrowCounterClockwise } from "@phosphor-icons/react";

const ACTION_META = {
  create: { label: "Created", icon: Plus, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  update: { label: "Updated", icon: PencilSimple, color: "text-sky-700 bg-sky-50 border-sky-200" },
  patch: { label: "Inline change", icon: LightningSlash, color: "text-violet-700 bg-violet-50 border-violet-200" },
  delete: { label: "Deleted", icon: Trash, color: "text-red-700 bg-red-50 border-red-200" },
  restore: { label: "Restored", icon: ArrowCounterClockwise, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};

const FIELD_LABELS = {
  module: "Module", object_id: "Object ID", object_name: "Object Name",
  object_desc: "Object Description", title: "Title", object_type: "Object Type",
  priority: "Priority", functional_owner: "Functional Owner", resource: "Resource",
  brd_status: "BRD Status", brd_date: "BRD Date",
  fdd_ready: "FDD Ready", fdd_released_date: "FDD Released Date",
  fdd_signoff_date: "FDD Sign Off Date",
  sit_status: "SIT Status", sit_completion_date: "SIT Completion Date",
  uat_status: "UAT Status", uat_completion_date: "UAT Completion Date",
  prod_migration: "Prod Migration", prod_migration_date: "Prod Migration Date",
  technical_resource: "Technical Resource", planned_dev_days: "Planned Dev Days",
  dev_start_date: "Dev Start Date", dev_end_date: "Dev End Date",
  target_date: "Target Date", development_status: "Development Status",
  actual_start_date: "Actual Start Date",
  actual_end_date: "Actual End Date", overall_status: "Overall Status",
  completion_pct: "Completion %", risk_level: "Risk Level",
  risk_description: "Risk Description", delay_days: "Delay (Days)",
  scope_of_works: "Scope of Works", comments: "Comments",
};

function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return String(v);
  return String(v);
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function AuditTrail({ taskId }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/tasks/${taskId}/audit`);
        if (!cancelled) setEntries(data.entries || []);
      } catch (e) {
        if (!cancelled) {
          setEntries([]);
          toast.error("Failed to load audit trail");
        }
      }
    };
    if (taskId) load();
    return () => { cancelled = true; };
  }, [taskId]);

  if (entries === null) {
    return <div className="text-sm text-neutral-500 py-8 text-center">Loading history…</div>;
  }

  if (entries.length === 0) {
    return (
      <div className="text-sm text-neutral-500 py-10 text-center flex flex-col items-center gap-2">
        <ClockClockwise size={28} weight="thin" className="text-neutral-400" />
        No history yet for this task.
      </div>
    );
  }

  return (
    <div className="relative pl-6" data-testid="audit-trail-list">
      <div className="absolute left-[9px] top-1 bottom-1 w-px bg-neutral-200" aria-hidden="true" />
      {entries.map((e, idx) => {
        const meta = ACTION_META[e.action] || ACTION_META.update;
        const Icon = meta.icon;
        return (
          <div key={e.id} className="relative pb-6" data-testid={`audit-entry-${idx}`}>
            <div className={`absolute -left-[15px] top-0.5 h-4 w-4 rounded-full border-2 border-white flex items-center justify-center ${meta.color}`}>
              <Icon size={9} weight="bold" />
            </div>
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.color}`}
                  data-testid={`audit-action-${idx}`}
                >
                  {meta.label}
                </span>
                <span className="text-sm font-medium text-neutral-900">{e.actor_email}</span>
                {e.actor_role ? (
                  <span className="text-[10px] uppercase tracking-wider text-neutral-400">
                    {e.actor_role}
                  </span>
                ) : null}
              </div>
              <span
                className="text-xs text-neutral-500 shrink-0"
                title={new Date(e.timestamp).toLocaleString()}
              >
                {timeAgo(e.timestamp)}
              </span>
            </div>
            {e.changes && e.changes.length > 0 ? (
              <div className="space-y-1 mt-2">
                {e.changes.map((c, i) => (
                  <div
                    key={i}
                    className="text-xs bg-neutral-50 border border-neutral-200 rounded px-2.5 py-1.5 flex items-center gap-2 flex-wrap"
                    data-testid={`audit-change-${idx}-${i}`}
                  >
                    <span className="font-semibold text-neutral-700 uppercase tracking-wider text-[10px]">
                      {FIELD_LABELS[c.field] || c.field}
                    </span>
                    <span className="text-neutral-500 line-through max-w-[240px] truncate">
                      {fmtVal(c.old)}
                    </span>
                    <span className="text-neutral-400">→</span>
                    <span className="text-[#0A0A0A] font-medium max-w-[240px] truncate">
                      {fmtVal(c.new)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-neutral-400 italic">No field changes recorded.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
